// Enhanced job matching with up-skills support
import { getRecommendationReasons } from '../utils/personalizedRecommendations';
import { calculateMatchScore } from './candidate-service';
import { getSafeSkillName } from '../utils/skillsUtils';
import cacheService from './cacheService.js';
import { authService, jobService } from './supabaseService.js';
import { jobMatchingLogger } from '../utils/logger';
import supabase from '../utils/supabase';

// Cache for job recommendations per VA
const jobRecommendationsCache = new Map();

// Dynamic skills list - will be enhanced to pull from database
let ALL_SKILLS = [
  'Excel', 'Google Sheets', 'Data Analysis', 'SQL', 'Python', 'WordPress',
  'Graphic Design', 'Social Media', 'Content Writing', 'Video Editing', 'SEO',
  'Email Management', 'Calendar Management', 'Customer Support', 'Research', 'Project Management'
];

/**
 * Get comprehensive skills list from database (enhanced for up-skills)
 */
const getAllSkillsFromDatabase = async () => {
  try {
    // Try to get from cache first
    if (cacheService.isAvailable()) {
      const cachedSkills = await cacheService.getSkillsList();
      if (cachedSkills) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[jobMatchingService] Skills loaded from cache');
        }
        return cachedSkills;
      }
    }

    const { data: skills, error } = await supabase
      .from('skills')
      .select('name')
      .order('name');
    
    if (error) {
      jobMatchingLogger.error('Failed to fetch skills from database', error);
      return ALL_SKILLS; // Fallback to hardcoded list
    }
    
    const skillNames = skills.map(skill => skill.name);
    // Merge with existing skills and remove duplicates
    const mergedSkills = [...new Set([...ALL_SKILLS, ...skillNames])];
    ALL_SKILLS = mergedSkills;

    // Cache the skills list
    if (cacheService.isAvailable()) {
      await cacheService.setSkillsList(mergedSkills);
      if (process.env.NODE_ENV === 'development') {
        console.log('[jobMatchingService] Skills cached');
      }
    }

    return mergedSkills;
  } catch (error) {
    jobMatchingLogger.error('Error fetching skills from database', error);
    return ALL_SKILLS;
  }
};

/**
 * Check if user profile uses the new up-skills format
 */
const hasUpSkillsFormat = (userProfile) => {
  const skills = userProfile?.skills || [];
  return Array.isArray(skills) && skills.some(skill => 
    typeof skill === 'object' && 
    skill !== null && 
    (skill.rating !== undefined || skill.upSkillsFormat === true)
  );
};

/**
 * Get user engagement history from the database
 * Retrieves real user behavior analytics from Supabase
 */
const getUserEngagementHistory = async (userId) => {
  try {
    // Get real user behavior analytics from the database
    const { data, error } = await supabase
      .from('user_behavior_analytics')
      .select('*')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false })
      .limit(50);
    
    if (error) {
      jobMatchingLogger.error('Error fetching user engagement history', error);
      return {}; // Return empty object on error
    }
    
    // Process the data into a more usable format for our recommendation algorithm
    const engagementHistory = {};
    
    data.forEach(entry => {
      if (entry.target_job_id) {
        engagementHistory[entry.target_job_id] = {
          jobId: entry.target_job_id,
          jobType: entry.metadata?.job_type || '',
          action: entry.behavior_type,
          employerId: entry.metadata?.employer_id || null,
          timestamp: new Date(entry.timestamp).getTime()
        };
      }
    });
    
    return engagementHistory;
  } catch (error) {
    jobMatchingLogger.error('Failed to retrieve engagement history', error);
    return {};
  }
};

/**
 * Get job recommendations for a VA
 * First checks existing job_matches table, then calculates fresh recommendations if needed
 */
export const getJobRecommendations = async (vaId) => {
  try {
    jobMatchingLogger.debug('getJobRecommendations called for VA ID', { vaId });
    
    // Fetch VA profile using Supabase service
    const { profile: vaProfile, error: profileError } = await authService.getUserProfile(vaId);
    if (profileError) {
      throw new Error(`Failed to fetch VA profile: ${profileError}`);
    }
    
    // Check if user has new up-skills format and clear cache if needed
    const useUpSkillsFormat = hasUpSkillsFormat(vaProfile);
    
    if (useUpSkillsFormat) {
      // Users with up-skills format get fresh recommendations to ensure proper matching
      jobMatchingLogger.debug('Detected up-skills format - clearing cache for fresh recommendations');
      if (cacheService.isAvailable()) {
        await cacheService.invalidateJobRecommendations(vaId);
      }
    } else {
      // Check Redis cache for users with legacy format
      if (cacheService.isAvailable()) {
        const cachedRecommendations = await cacheService.getJobRecommendations(vaId);
        if (cachedRecommendations) {
          jobMatchingLogger.debug('Returning cached job recommendations', { count: cachedRecommendations.length });
          return cachedRecommendations;
        }
      }
    }
    
    jobMatchingLogger.debug('Fetching fresh job recommendations');

    // Get dynamic skills list from database for better matching
    const dynamicSkills = await getAllSkillsFromDatabase();
    
    // Check for existing job matches in the database
    jobMatchingLogger.debug('Fetching job matches from database for candidate ID', { vaId });
    const { data: existingMatches, error: matchesError } = await supabase
      .from('job_matches')
      .select(`
        id,
        job_id,
        match_score,
        match_factors,
        recommendation_reason,
        jobs(
          *,
          user_profiles!jobs_employer_id_fkey(company_name, profile_picture)
        )
      `)
      .eq('candidate_id', vaId)
      .eq('is_recommended', true)
      .order('match_score', { ascending: false })
      .limit(20);
      
    if (matchesError) {
      jobMatchingLogger.error('Failed to fetch existing job matches', matchesError);
      // Continue with calculation if we can't fetch existing matches
    } else if (existingMatches && existingMatches.length > 0 && !useUpSkillsFormat) {
      // Only use existing matches for legacy format users
      jobMatchingLogger.debug('Found existing job matches in database', { count: existingMatches.length });
      jobMatchingLogger.debug('Sample match data', existingMatches[0]);
      
      // Process and return existing job matches
      const recommendations = existingMatches.map(match => {
        const job = match.jobs;
        if (!job) {
          jobMatchingLogger.warn('Job data missing for match', { matchId: match.id, jobId: match.job_id });
          return null;
        }
        return {
          ...job,
          matchScore: match.match_score,
          matchFactors: match.match_factors,
          recommendationReasons: match.recommendation_reason ? 
            [match.recommendation_reason] : 
            getRecommendationReasons(vaProfile, job, match.match_score)
        };
      })
      .filter(job => job !== null)
      .filter(job => ['published', 'active'].includes(job.status))
      .filter(job => job.matchScore > 20);
      
      // If we have at least 5 recommendations, use them
      if (recommendations.length >= 5) {
        jobMatchingLogger.debug('Using existing job matches', { count: recommendations.length });
        // Cache for 5 minutes with Redis
        if (cacheService.isAvailable()) {
          await cacheService.setJobRecommendations(vaId, recommendations);
        }
        return recommendations;
      } else {
        jobMatchingLogger.debug('Not enough existing job matches with score > 20', { count: recommendations.length });
        // Continue to calculate fresh recommendations
      }
    } else {
      jobMatchingLogger.debug(useUpSkillsFormat ? 
        'Up-skills format detected - calculating fresh recommendations' : 
        'No existing job matches found in database'
      );
    }
    
    // Calculate fresh recommendations
    jobMatchingLogger.debug('Calculating fresh job recommendations with enhanced skill matching');
    
    // Fetch all active jobs using Supabase service
    const { jobs, error: jobsError } = await jobService.getActiveJobs({ limit: 100 });
    if (jobsError) {
      throw new Error(`Failed to fetch jobs: ${jobsError}`);
    }
    
    jobMatchingLogger.debug('Found active jobs to evaluate', { count: jobs.length });
    
    // Create recommendations using simple match score calculation for consistency with job-search
    const recommendations = jobs.map(job => {
      // Prepare candidate object with normalized skills
      const candidate = {
        skills: (vaProfile?.skills || []).map(skill => getSafeSkillName(skill)),
        experience_years: vaProfile?.experience_years || 0
      };

      // Prepare job object with normalized skills
      const jobData = {
        skills: (job?.skills_required || job?.required_skills || job?.skills || []).map(skill => {
          return typeof skill === 'string' ? skill : skill?.name || '';
        }).filter(s => s) // Remove empty strings
      };

      // Calculate match score using the same algorithm as job-search
      const matchScore = calculateMatchScore(candidate, jobData);
      
      // Get recommendation reasons
      const reasons = getRecommendationReasons(vaProfile, job, matchScore);
      
      return { 
        ...job, 
        matchScore, 
        recommendationReasons: reasons
      };
    })
    .sort((a, b) => b.matchScore - a.matchScore)
    .filter(job => ['published', 'active'].includes(job.status))
    .filter(job => {
      const passes = job.matchScore > 20;
      if (!passes) {
        jobMatchingLogger.debug('Filtering out job with low match score', { 
          jobId: job.id, 
          matchScore: job.matchScore,
          threshold: 20
        });
      }
      return passes;
    }); // Only show jobs with >20% match
    
    jobMatchingLogger.debug('After filtering, found jobs with match score > 20%', { count: recommendations.length });
    
    // Update job_matches table with the new recommendations
    await updateJobMatches(vaId, recommendations);
    
    // Cache the results with Redis
    if (cacheService.isAvailable()) {
      await cacheService.setJobRecommendations(vaId, recommendations);
      if (process.env.NODE_ENV === 'development') {
        console.log('[jobMatchingService] Recommendations cached for VA:', vaId);
      }
    }
    
    return recommendations;
  } catch (error) {
    jobMatchingLogger.error('Job matching failed', error);
    throw new Error('Failed to get job recommendations');
  }
};

/**
 * Update job matches in the database
 * This creates or updates entries in the job_matches table
 */
const updateJobMatches = async (candidateId, recommendations) => {
  try {
    if (!candidateId || !recommendations || !recommendations.length) {
      return;
    }

    // Get existing matches to avoid duplicates
    const { data: existingMatches, error: existingError } = await supabase
      .from('job_matches')
      .select('job_id')
      .eq('candidate_id', candidateId);
    
    if (existingError) {
      jobMatchingLogger.error('Error fetching existing matches', existingError);
      return;
    }
    
    // Create a map of existing job matches
    const existingJobMatches = new Map();
    existingMatches?.forEach(match => {
      existingJobMatches.set(match.job_id, true);
    });
    
    // Prepare updates and inserts
    const updates = [];
    const inserts = [];
    
    // Process top 20 recommendations
    recommendations.slice(0, 20).forEach((job, index) => {
      const jobId = job.id;
      
      if (!jobId) return;
      
      // Basic match factors for database
      const matchFactors = {
        skills_match: job.matchScore / 100, // Convert to 0-1 scale
        experience_match: 0,
        rate_match: 0,
        location_match: 0
      };
      
      // Get the first recommendation reason (if any)
      const recommendationReason = job.recommendationReasons && job.recommendationReasons.length > 0 
        ? job.recommendationReasons[0] 
        : null;
      
      if (existingJobMatches.has(jobId)) {
        // Update existing match
        updates.push({
          job_id: jobId,
          candidate_id: candidateId,
          match_score: Math.round(job.matchScore),
          match_factors: matchFactors,
          recommendation_reason: recommendationReason,
          recommendation_rank: index + 1,
          is_recommended: true,
          updated_at: new Date().toISOString()
        });
      } else {
        // Insert new match
        inserts.push({
          job_id: jobId,
          candidate_id: candidateId,
          match_score: Math.round(job.matchScore),
          match_factors: matchFactors,
          recommendation_reason: recommendationReason,
          // Remove recommendation_rank as it's causing a 400 error
          is_recommended: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
    });
    
    // Execute updates
    if (updates.length > 0) {
      // Use transaction for multiple updates
      const { error: updateError } = await supabase.rpc('update_job_matches', { 
        match_updates: updates 
      });
      
      if (updateError) {
        jobMatchingLogger.error('Error updating job matches', updateError);
      }
    }
    
    // Execute inserts
    if (inserts.length > 0) {
      const { error: insertError } = await supabase
        .from('job_matches')
        .insert(inserts);
      
      if (insertError) {
        jobMatchingLogger.error('Error inserting job matches', insertError);
      }
    }
  } catch (error) {
    jobMatchingLogger.error('Failed to update job matches', error);
  }
};

export const getJobDetails = async (jobId) => {
  try {
    const { job, error } = await jobService.getJobById(jobId);
    if (error) {
      throw new Error(`Failed to fetch job details: ${error}`);
    }
    return job;
  } catch (error) {
    jobMatchingLogger.error('Failed to fetch job details', error);
    throw error;
  }
};

// Clear cache when VA profile is updated
export const clearJobRecommendationsCache = async (vaId) => {
  if (cacheService.isAvailable()) {
    await cacheService.invalidateJobRecommendations(vaId);
  }
  jobMatchingLogger.debug('Cleared job recommendations cache for VA', { vaId });
};

// Update job recommendations when profile changes (enhanced for up-skills)
export const refreshJobRecommendations = async (vaId) => {
  try {
    // Get user profile to check format
    const { profile: vaProfile, error: profileError } = await authService.getUserProfile(vaId);
    if (profileError) {
      jobMatchingLogger.error('Failed to fetch VA profile for refresh', profileError);
      return { success: false, error: profileError };
    }
    
    // Clear cache first
    await clearJobRecommendationsCache(vaId);
    
    // Log if user has up-skills format for better debugging
    const useUpSkillsFormat = hasUpSkillsFormat(vaProfile);
    jobMatchingLogger.debug('Refreshing recommendations for VA', { vaId, useUpSkillsFormat });
    
    // Fetch fresh recommendations
    await getJobRecommendations(vaId);
    
    return { success: true, upSkillsFormat: useUpSkillsFormat };
  } catch (error) {
    jobMatchingLogger.error('Failed to refresh job recommendations', error);
    return { success: false, error: error.message };
  }
};

// Enhanced cache clearing that also considers up-skills format
export const clearJobRecommendationsCacheForUpSkillsUsers = async (vaId) => {
  try {
    const { profile: vaProfile } = await authService.getUserProfile(vaId);
    if (vaProfile && hasUpSkillsFormat(vaProfile)) {
      await clearJobRecommendationsCache(vaId);
      jobMatchingLogger.debug('Cleared cache for up-skills user', { vaId });
      return true;
    }
    return false;
  } catch (error) {
    jobMatchingLogger.error('Error checking up-skills format for cache clearing', error);
    return false;
  }
};
